import Section, { SectionVisibilityProps } from './Section';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PaymentMethod from '../../payment/payment-methods/PaymentMethod';
import { PaymentGateway, PaymentMethod as PaymentMethodEnum, PaypalVariant } from '../../../services/exports/Constants';
import { castAdyenPaymentMethodType } from '../../../services/helpers/adyenPaymentMethod';
import classnames from 'classnames';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import PaymentMethodBrandIcons from '../../payment/payment-methods/PaymentMethodBrandIcons';
import { SectionType } from './CheckoutInfoBag';
import { usePaymentCoordinator } from '../../../hooks/payment/usePaymentCoordinator';
import PaymentInitializer from '../../payment/PaymentInitializer';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../store/types';
import { ApplePayConfiguration, Dropin, GooglePayConfiguration, UIElement } from '@adyen/adyen-web/auto';
import useDeepRef from '../../../hooks/utility/useDeepRef';
import { PaymentOptionPropertiesInterface } from '../../../services/exports/Interfaces';
import collect from 'collect.js';
import PaymentMethodOption, { Variant } from '../../payment/payment-methods/PaymentMethodOption';
import CashTableNote from '../../payment/payment-methods/CashTableNote';
import PaymentManager from '../../../services/api/PaymentManager';
import useBillingInfo from '../../../hooks/payment/useBillingInfo';
import { ChevronRight, Lock } from 'lucide-react';
import LoadingIndicator from '../../loading/LoadingIndicator';
import TermsLink from '../../legal/TermsLink';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import StripeElementsPaymentMethods, {
  StripeElementsSkeleton,
} from '../../payment/stripe-elements/StripeElementsPaymentMethods';

type Props = SectionVisibilityProps & {
  showTerms?: boolean;
  excludedPaymentMethods?: PaymentMethodEnum[];
  className?: string;
};

type PaymentListProps = {
  loading: boolean;
  filteredPaymentOpts: PaymentOptionPropertiesInterface[];
};

export default function PaymentInfoSection({ show, showTerms, className, excludedPaymentMethods = [] }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:PaymentInfoSection' });

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { loading } = usePaymentCoordinator();
  const { paymentOptions, hasOnlinePayments, selectedPaymentMethod, setSelectedPaymentMethod } = usePaymentFlow();

  const filteredPaymentOpts = useMemo<PaymentOptionPropertiesInterface[]>(
    () => paymentOptions.filter((item) => !excludedPaymentMethods.includes(item.payment_method_id)),
    [paymentOptions, excludedPaymentMethods],
  );

  // The selection is shared with the food checkout and persisted, so a method this checkout
  // excludes (an offsite method picked for an order, then a gift card) can arrive selected: hidden from
  // the list, yet what Pay would charge. Moved to the first method this checkout offers.
  const paymentGateway = company?.payment_gateways?.[0];
  const useAdyenUi = hasOnlinePayments && paymentGateway === PaymentGateway.Adyen;
  const useStripeUi = hasOnlinePayments && paymentGateway === PaymentGateway.Stripe;

  useEffect(() => {
    if (!selectedPaymentMethod || !excludedPaymentMethods.includes(selectedPaymentMethod)) {
      return;
    }

    // Under the Stripe Elements checkout the first offered method is often PayPal, folded away in
    // its accordion; the customer picks from what they can see instead.
    setSelectedPaymentMethod(useStripeUi ? null : (filteredPaymentOpts[0]?.payment_method_id ?? null));
  }, [selectedPaymentMethod, excludedPaymentMethods, filteredPaymentOpts, useStripeUi]);

  const props = { loading, filteredPaymentOpts };
  const methods = useAdyenUi ? (
    <AdyenPaymentMethods {...props} />
  ) : useStripeUi ? (
    <StripeElementsPaymentMethods {...props} />
  ) : (
    // Spreedly, and a restaurant that takes no online payments (cash only) on any gateway.
    <PaymentMethodRows {...props} />
  );

  const section = (content: React.ReactNode) => (
    <Section
      show={show}
      id={SectionType.payment_info}
      title={t('title')}
      description={
        <span className="flex gap-2">
          <Lock className="w-3.5 h-3.5" /> {t('description')}
        </span>
      }
      className={classnames('p-none', className)}
    >
      {content}
      {showTerms && <TermsLink />}
    </Section>
  );

  // Outside the Stripe Elements checkout (Adyen, Spreedly, cash only) the section stays away until the
  // payment set-up is ready, as it always has.
  if (!useStripeUi) {
    return <PaymentInitializer fallback={<></>}>{section(methods)}</PaymentInitializer>;
  }

  // The Stripe Elements checkout draws the section at once, its methods' places included, while the
  // payment set-up gets ready: an empty column there read as a page that had lost its payment methods.
  return section(
    <PaymentInitializer fallback={<StripeElementsSkeleton filteredPaymentOpts={filteredPaymentOpts} />}>
      {methods}
    </PaymentInitializer>,
  );
}

const AdyenPaymentMethods = ({ loading, filteredPaymentOpts }: PaymentListProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:PaymentInfoSection' });
  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { selectedPaymentMethod, paypalVariant, setSelectedPaymentMethod } = usePaymentFlow();
  const { paymentGatewaySDK, isPaymentProcessing } = usePaymentCoordinator(PaymentGateway.Adyen);
  const { initialize, paymentElement, paymentMethodsResponse, setPaymentElement } = paymentGatewaySDK;

  const [paymentsMounted, setPaymentsMounted] = useState(false);
  const adyenContainerRef = useRef(null);
  const paymentElementRef = useDeepRef(paymentElement);
  const cashPaymentOption = useMemo<PaymentOptionPropertiesInterface | null>(
    () => collect(filteredPaymentOpts).firstWhere('payment_method_id', PaymentMethodEnum.Cash),
    [filteredPaymentOpts],
  );
  const paypalPaymentOption = useMemo<PaymentOptionPropertiesInterface | null>(
    () => collect(filteredPaymentOpts).firstWhere('payment_method_id', PaymentMethodEnum.Paypal),
    [filteredPaymentOpts],
  );

  // One PayPal entry, routed: direct → SDK buttons row; via-stripe → the current
  // secondary-gateway accordion (prod behaviour).
  const showPaypalDirect = paypalVariant === PaypalVariant.Direct && Boolean(paypalPaymentOption);
  const showPaypalViaStripe =
    paypalVariant === PaypalVariant.ViaStripe &&
    company?.payment_gateways?.includes(PaymentGateway.Stripe) &&
    Boolean(paypalPaymentOption);
  const applePayConfig = useMemo<null | Partial<ApplePayConfiguration>>(
    () => collect(paymentMethodsResponse?.paymentMethods ?? []).firstWhere('type', 'applepay'),
    [paymentMethodsResponse],
  );
  const googlePayConfig = useMemo<null | Partial<GooglePayConfiguration>>(
    () => collect(paymentMethodsResponse?.paymentMethods ?? []).firstWhere('type', 'googlepay'),
    [paymentMethodsResponse],
  );

  const { billingFullName, adyenAddressFormat } = useBillingInfo();

  const isInitializingRef = useRef(false);

  // The Dropin opens the first stored method on mount (openFirstStoredPaymentMethod), so the selection
  // follows it, including from no selection at all: PaymentFlowProvider clears a stale PayPal pick on
  // Adyen restaurants, and Pay would otherwise stay disabled under an open stored card.
  useEffect(() => {
    if (paymentsMounted && !!paymentMethodsResponse) {
      setSelectedPaymentMethod(
        paymentMethodsResponse?.storedPaymentMethods?.length > 0
          ? castPaymentMethodType(paymentMethodsResponse.storedPaymentMethods[0].type)
          : null,
      );
    }
  }, [paymentsMounted, paymentMethodsResponse?.storedPaymentMethods?.length]);

  function castPaymentMethodType(adyenType: string) {
    const type = castAdyenPaymentMethodType(adyenType);

    if (!type) {
      console.error('Unknown adyen payment method', { adyenType });
    }

    return type;
  }

  const onSelectAdyenPaymentMethod = (adyenType: string) => {
    const type = castPaymentMethodType(adyenType);

    if (!type) {
      return console.error('Unknown adyen payment method', { adyenType });
    }

    setSelectedPaymentMethod(type);
  };

  const fetchGooglePayJwt = async (): Promise<string | undefined> => {
    if (!googlePayConfig || !company?.id) return undefined;

    const { success, response } = await PaymentManager.getGooglePayJwt({
      company_id: company.id,
      origin: window.location.host,
    });

    return success ? response.data.jwt : undefined;
  };

  const initDropin = async () => {
    // Prevent double init
    if (paymentElementRef.current || isInitializingRef.current) return;

    if (!adyenContainerRef.current || !initialize) return;

    isInitializingRef.current = true;

    try {
      const [checkout, googlePayAuthJwt] = await Promise.all([initialize, fetchGooglePayJwt()]);

      // The container can unmount while the async init above is in flight (iOS Safari
      // bfcache restore / remount, navigation away). Mounting onto a now-null node makes
      // Adyen throw an unhandled "Component could not mount. Root node was not found." —
      // bail out instead so the Drop-in re-inits cleanly on the next mount.
      if (!adyenContainerRef.current) {
        return;
      }

      const dropin = new Dropin(checkout, {
        openFirstPaymentMethod: false,
        openFirstStoredPaymentMethod: true,
        showRemovePaymentMethodButton: true,
        showRadioButton: true,
        instantPaymentTypes: ['applepay', 'googlepay'],
        paymentMethodsConfiguration: {
          card: {
            hasHolderName: true,
            holderNameRequired: true,
            billingAddressRequired: true,
            billingAddressMode: 'full',
            data: {
              holderName: billingFullName,
              billingAddress: adyenAddressFormat,
            },
          },
          applepay: applePayConfig
            ? {
                configuration: {
                  merchantName: company?.name,
                  merchantId: applePayConfig.configuration.merchantId,
                },
              }
            : undefined,
          googlepay: googlePayConfig
            ? {
                configuration: {
                  merchantName: company?.name,
                  gatewayMerchantId: googlePayConfig.configuration.gatewayMerchantId,
                  merchantId: googlePayConfig.configuration.merchantId,
                  merchantOrigin: window.location.host,
                  authJwt: googlePayAuthJwt,
                },
              }
            : undefined,
        },
        onSelect: (paymentMethod: UIElement) => onSelectAdyenPaymentMethod(paymentMethod.type),
        onDisableStoredPaymentMethod: async (storedPaymentMethodId, resolve, reject) => {
          const { success } = await PaymentManager.deleteAdyenPaymentMethod(storedPaymentMethodId, {
            company_id: company?.id,
          });

          success ? resolve() : reject();
        },
        onReady: () => setPaymentsMounted(true),
        onError: (error) => console.error({ error }),
      });

      dropin.mount(adyenContainerRef.current);
      setPaymentElement(dropin);
    } finally {
      isInitializingRef.current = false;
    }
  };

  useEffect(() => {
    initDropin();

    return () => {
      if (paymentElementRef.current) {
        paymentElementRef.current.unmount();
        paymentElementRef.current = null;
      }
    };
  }, [initialize]);

  return (
    <div>
      {!paymentsMounted && (
        <div className="flex justify-center py-lg">
          <LoadingIndicator />
        </div>
      )}
      <div ref={adyenContainerRef} />
      {Boolean(cashPaymentOption) && paymentsMounted && (
        <PaymentMethodOption
          id={PaymentMethodEnum.Cash}
          name={cashPaymentOption.name}
          extra={<PaymentMethodBrandIcons {...cashPaymentOption} className="h-[18px] my-auto" />}
          isLastItem
          isSelected={selectedPaymentMethod === PaymentMethodEnum.Cash}
          isDisabled={loading || isPaymentProcessing}
          onClick={() => {
            paymentElementRef.current && paymentElementRef.current.closeActivePaymentMethod();
            setSelectedPaymentMethod(PaymentMethodEnum.Cash);
          }}
          key={`payment-method-option-${PaymentMethodEnum.Cash}`}
          variant={Variant.Adyen}
        >
          {/* The Adyen row draws no `content`; its children sit under it. */}
          <CashTableNote className="mt-0 px-md pb-md" />
        </PaymentMethodOption>
      )}
      {(showPaypalDirect || showPaypalViaStripe) && paymentsMounted && (
        // Both PayPal variants live under the same "Show more payment methods" collapse,
        // for consistent placement in the Adyen UI. Selecting the row sets PayPal as the
        // method; for direct the PayPal SDK button then renders in the bottom pay-button
        // slot (PayButton).
        <Accordion
          disableGutters
          elevation={0}
          className={paypalAccordion}
          style={{ boxShadow: 'none', backgroundColor: 'transparent' }}
        >
          <AccordionSummary
            expandIcon={<ChevronRight className="rotate-90 w-4 h-4" />}
            style={{ padding: 0, minHeight: 0 }}
            classes={{ content: '!m-none !py-sm text-sm' }}
          >
            {t('show_more_payment_methods')}
          </AccordionSummary>
          <AccordionDetails style={{ padding: 0 }}>
            <PaymentMethodOption
              id={PaymentMethodEnum.Paypal}
              name={paypalPaymentOption.name}
              extra={<PaymentMethodBrandIcons {...paypalPaymentOption} className="h-[18px] my-auto" />}
              isLastItem
              isSelected={selectedPaymentMethod === PaymentMethodEnum.Paypal}
              isDisabled={loading || isPaymentProcessing}
              onClick={() => {
                paymentElementRef.current && paymentElementRef.current.closeActivePaymentMethod();
                setSelectedPaymentMethod(PaymentMethodEnum.Paypal);
              }}
              key={`payment-method-option-${PaymentMethodEnum.Paypal}`}
              variant={Variant.Adyen}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};

const paypalAccordion = 'mt-md !rounded-none !bg-transparent';

const PaymentMethodRows = ({ loading, filteredPaymentOpts }: PaymentListProps) => {
  const { selectedPaymentMethod, setSelectedPaymentMethod } = usePaymentFlow();

  return filteredPaymentOpts.map((item, index) => {
    const isSelected = item.payment_method_id === selectedPaymentMethod;
    const isNextSelected = filteredPaymentOpts[index + 1]?.payment_method_id === selectedPaymentMethod;

    return (
      <PaymentMethod
        id={item.payment_method_id}
        name={item.name}
        extra={<PaymentMethodBrandIcons {...item} className="h-4 my-auto" />}
        isLastItem={index === filteredPaymentOpts.length - 1}
        isSelected={isSelected}
        isDisabled={loading}
        onClick={() => setSelectedPaymentMethod(item.payment_method_id)}
        key={`payment-method-option-${item.payment_method_id}`}
        className={classnames('bg-white', {
          'border-t-none': index > 0 && !isSelected,
          'border-b-none': isNextSelected,
          'rounded-t-2xl': index === 0,
          'rounded-b-2xl': index === filteredPaymentOpts.length - 1,
        })}
      />
    );
  });
};
